/**
 * Client-safe auth fields for browser console, client logger, and NR Browser.
 * Full email / userId / accountId belong on the server only (Pino → NR Logs, Supabase dashboard).
 */

/** Prefix length for correlating with server logs and Supabase lookup. */
export const AUTH_LOG_USER_ID_PREFIX_LENGTH = 12

export function getAuthLogUserIdPrefix(userId: string | null | undefined): string | null {
  if (!userId || typeof userId !== 'string') return null
  const trimmed = userId.trim()
  if (!trimmed) return null
  if (trimmed.length <= AUTH_LOG_USER_ID_PREFIX_LENGTH) return trimmed
  return `${trimmed.slice(0, AUTH_LOG_USER_ID_PREFIX_LENGTH)}…`
}

export function getAuthEmailDomain(email: string | null | undefined): string | null {
  if (!email || typeof email !== 'string') return null
  const at = email.indexOf('@')
  if (at <= 0 || at >= email.length - 1) return null
  const domain = email.slice(at + 1).trim().toLowerCase()
  return domain.length > 0 ? domain : null
}

/** Short id to tie together all [AUTH-LIVE-DEBUG] lines for one sign-in attempt. */
export function createAuthAttemptId(): string {
  const tail =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID().slice(0, 8)
      : `${Math.random().toString(36).slice(2, 10)}`
  return `auth-${Date.now().toString(36)}-${tail}`
}

type AuthObservabilityUserInput = {
  id?: string
  email?: string | null
  user_metadata?: Record<string, unknown>
}

/** Fields safe for client-side logs and NR Browser (no full email, userId, or accountId). */
export interface AuthUserClientObservabilityFields {
  userIdPrefix: string | null
  emailDomain: string | null
  displayName: string | null
}

function getAuthEmailFromUser(
  user: AuthObservabilityUserInput | null | undefined
): string | null {
  if (!user) return null
  const raw =
    user.email ??
    (typeof user.user_metadata?.email === 'string' ? user.user_metadata.email : null)
  if (!raw || typeof raw !== 'string') return null
  const trimmed = raw.trim()
  return trimmed.length > 0 ? trimmed : null
}

/** Display name from OAuth metadata — not the login email. */
export function getAuthDisplayName(
  user: AuthObservabilityUserInput | null | undefined
): string | null {
  if (!user) return null
  const meta = user.user_metadata ?? {}
  const fullName =
    typeof meta.full_name === 'string'
      ? meta.full_name.trim()
      : typeof meta.name === 'string'
        ? meta.name.trim()
        : ''
  if (fullName) return fullName
  const username =
    (typeof meta.user_name === 'string' && meta.user_name.trim()) ||
    (typeof meta.preferred_username === 'string' && meta.preferred_username.trim()) ||
    (typeof meta.username === 'string' && meta.username.trim()) ||
    null
  if (username) return username
  const first = typeof meta.first_name === 'string' ? meta.first_name.trim() : ''
  const last = typeof meta.last_name === 'string' ? meta.last_name.trim() : ''
  const combined = [first, last].filter(Boolean).join(' ')
  return combined.length > 0 ? combined : null
}

/** Client-only: prefix + domain + display name. */
export function getAuthUserClientObservabilityFields(
  user: AuthObservabilityUserInput | null | undefined
): AuthUserClientObservabilityFields {
  const email = getAuthEmailFromUser(user)
  return {
    userIdPrefix: user?.id ? getAuthLogUserIdPrefix(user.id) : null,
    emailDomain: getAuthEmailDomain(email),
    displayName: getAuthDisplayName(user),
  }
}

/** @alias getAuthUserClientObservabilityFields */
export function getAuthUserObservabilityFields(
  user: AuthObservabilityUserInput | null | undefined
): AuthUserClientObservabilityFields {
  return getAuthUserClientObservabilityFields(user)
}

/** @alias getAuthUserClientObservabilityFields */
export function getAuthUserLogFields(
  user: AuthObservabilityUserInput | null | undefined
): AuthUserClientObservabilityFields {
  return getAuthUserClientObservabilityFields(user)
}

export function getAuthObservabilityFromSupabaseSessionUser(
  user: AuthObservabilityUserInput | null | undefined
): AuthUserClientObservabilityFields {
  return getAuthUserClientObservabilityFields(user)
}
